import React, { useEffect, useState } from 'react';
import { usersAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate, ROLE_LABELS } from '../../lib/utils';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { User, Shield, MoreVertical, Trash2, UserCheck, Clock, CheckCircle, KeyRound } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

export const UsersAdmin = () => {
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [pwdResetUser, setPwdResetUser] = useState(null);
  const [newPwd, setNewPwd] = useState('');
  const [newPwd2, setNewPwd2] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'super_admin';

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const [allRes, pendingRes] = await Promise.all([
        usersAPI.getAll(),
        usersAPI.getPending()
      ]);
      setUsers(allRes.data.filter(u => u.is_approved));
      setPendingUsers(pendingRes.data);
    } catch (error) {
      toast.error('Nepodařilo se načíst uživatele');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await usersAPI.approve(userId);
      toast.success('Uživatel byl schválen');
      loadUsers();
    } catch (error) {
      toast.error('Nepodařilo se schválit uživatele');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await usersAPI.delete(deleteId);
      setUsers(users.filter(u => u.id !== deleteId));
      setPendingUsers(pendingUsers.filter(u => u.id !== deleteId));
      toast.success('Uživatel byl smazán');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nepodařilo se smazat uživatele');
    } finally {
      setDeleteId(null);
    }
  };

  const handleUpdateUser = async (userId, data) => {
    try {
      await usersAPI.update(userId, data);
      setUsers(users.map(u => u.id === userId ? { ...u, ...data } : u));
      toast.success('Uživatel byl aktualizován');
      setEditUser(null);
    } catch (error) {
      toast.error('Nepodařilo se aktualizovat uživatele');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#2D3748]">Uživatelé</h1>
        <p className="text-[#64748B]">Správa uživatelských účtů a oprávnění</p>
      </div>

      <Tabs defaultValue={pendingUsers.length > 0 ? "pending" : "approved"} className="space-y-6">
        <TabsList className="bg-white border-2 border-[#F0F4F8] p-1 rounded-xl">
          <TabsTrigger 
            value="pending" 
            className="rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#FF6B6B] data-[state=active]:to-[#FF9F43] data-[state=active]:text-white"
          >
            <Clock size={16} className="mr-2" />
            Čekající na schválení ({pendingUsers.length})
          </TabsTrigger>
          <TabsTrigger 
            value="approved" 
            className="rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#45AAF2] data-[state=active]:to-[#26DE81] data-[state=active]:text-white"
          >
            <CheckCircle size={16} className="mr-2" />
            Schválení uživatelé ({users.length})
          </TabsTrigger>
        </TabsList>

        {/* Pending Users */}
        <TabsContent value="pending">
          {pendingUsers.length > 0 ? (
            <div className="grid gap-4">
              {pendingUsers.map((user) => (
                <div 
                  key={user.id}
                  className="bg-white rounded-2xl border-3 border-[#FF6B6B]/30 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                  data-testid={`pending-user-${user.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF6B6B] to-[#FF9F43] flex items-center justify-center text-white">
                      <User size={20} />
                    </div>
                    <div>
                      <div className="font-bold text-[#2D3748]">{user.name}</div>
                      <div className="text-sm text-[#64748B]">{user.email}</div>
                      <div className="text-xs text-[#64748B] mt-1">
                        Registrace: {formatDate(user.created_at)}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleApprove(user.id)}
                      className="rounded-full bg-gradient-to-r from-[#26DE81] to-[#45AAF2] hover:opacity-90"
                      data-testid={`approve-user-${user.id}`}
                    >
                      <UserCheck size={16} className="mr-2" />
                      Schválit
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setDeleteId(user.id)}
                      className="rounded-full text-[#FF6B6B] border-[#FF6B6B] hover:bg-[#FF6B6B]/10"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-2xl border-2 border-[#F0F4F8]">
              <CheckCircle size={48} className="mx-auto text-[#26DE81] mb-4" />
              <div className="text-[#64748B] font-medium">Žádní uživatelé nečekají na schválení</div>
            </div>
          )}
        </TabsContent>

        {/* Approved Users */}
        <TabsContent value="approved">
          <div className="bg-white rounded-2xl border-2 border-[#F0F4F8] overflow-hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Uživatel</th>
                  <th className="hidden md:table-cell">Email</th>
                  <th>Role</th>
                  <th className="hidden md:table-cell">Stav</th>
                  <th className="w-20">Akce</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} data-testid={`user-row-${user.id}`}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#45AAF2] to-[#26DE81] flex items-center justify-center text-white flex-shrink-0">
                          <User size={16} />
                        </div>
                        <div>
                          <div className="font-bold text-[#2D3748]">{user.name}</div>
                          <div className="text-xs text-[#64748B] md:hidden">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="hidden md:table-cell text-[#64748B]">{user.email}</td>
                    <td>
                      <span className={`badge ${
                        user.role === 'super_admin' ? 'badge-info' :
                        user.role === 'admin' ? 'badge-success' :
                        user.role === 'editor' ? 'badge-warning' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {ROLE_LABELS[user.role] || user.role}
                      </span>
                    </td>
                    <td className="hidden md:table-cell">
                      <span className={`badge ${user.is_active ? 'badge-success' : 'badge-error'}`}>
                        {user.is_active ? 'Aktivní' : 'Neaktivní'}
                      </span>
                    </td>
                    <td>
                      {user.id !== currentUser?.id && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0"><MoreVertical size={16} /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setEditUser(user)} className="cursor-pointer">
                              <Shield size={14} className="mr-2" />Upravit oprávnění
                            </DropdownMenuItem>
                            {isSuperAdmin && user.id !== currentUser?.id && (
                              <DropdownMenuItem
                                onClick={() => { setPwdResetUser(user); setNewPwd(''); setNewPwd2(''); }}
                                className="cursor-pointer"
                                data-testid={`reset-pwd-${user.id}`}
                              >
                                <KeyRound size={14} className="mr-2" />Resetovat heslo
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => setDeleteId(user.id)} className="cursor-pointer text-red-600">
                              <Trash2 size={14} className="mr-2" />Smazat
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Role Permissions Info */}
      <div className="bg-gradient-to-br from-[#FFFDF7] to-[#E8F4FD] rounded-2xl p-6 border-2 border-[#F0F4F8]">
        <h3 className="font-bold text-[#2D3748] mb-4">📋 Přehled oprávnění</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 border-2 border-[#45AAF2]/20">
            <div className="badge badge-info mb-2">Super Admin</div>
            <p className="text-sm text-[#64748B]">Plný přístup ke všemu včetně schvalování uživatelů</p>
          </div>
          <div className="bg-white rounded-xl p-4 border-2 border-[#26DE81]/20">
            <div className="badge badge-success mb-2">Administrátor</div>
            <p className="text-sm text-[#64748B]">Správa uživatelů a veškerého obsahu</p>
          </div>
          <div className="bg-white rounded-xl p-4 border-2 border-[#FECA57]/20">
            <div className="badge badge-warning mb-2">Editor</div>
            <p className="text-sm text-[#64748B]">Vytváření a úprava obsahu</p>
          </div>
          <div className="bg-white rounded-xl p-4 border-2 border-[#64748B]/20">
            <div className="badge bg-slate-100 text-slate-600 mb-2">Učitelka</div>
            <p className="text-sm text-[#64748B]">Čtení a přidávání fotek/aktualit</p>
          </div>
        </div>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upravit uživatele</DialogTitle>
          </DialogHeader>
          {editUser && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-[#E8F4FD] to-[#E6FFED] rounded-xl">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#45AAF2] to-[#26DE81] flex items-center justify-center text-white">
                  <User size={18} />
                </div>
                <div>
                  <div className="font-bold">{editUser.name}</div>
                  <div className="text-sm text-[#64748B]">{editUser.email}</div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Role</Label>
                <Select 
                  value={editUser.role} 
                  onValueChange={(value) => setEditUser({ ...editUser, role: value })}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                    <SelectItem value="admin">Administrátor</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                    <SelectItem value="kitchen_manager">Vedoucí školní jídelny</SelectItem>
                    <SelectItem value="deputy_director">Zástupkyně ředitele</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between p-4 bg-[#F0F4F8] rounded-xl">
                <div>
                  <div className="font-bold text-[#2D3748]">Aktivní účet</div>
                  <div className="text-sm text-[#64748B]">Uživatel se může přihlásit</div>
                </div>
                <Switch 
                  checked={editUser.is_active} 
                  onCheckedChange={(checked) => setEditUser({ ...editUser, is_active: checked })} 
                />
              </div>

              <Button 
                onClick={() => handleUpdateUser(editUser.id, { role: editUser.role, is_active: editUser.is_active })}
                className="w-full rounded-full bg-gradient-to-r from-[#45AAF2] to-[#26DE81] hover:opacity-90"
              >
                Uložit změny
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Smazat uživatele?</AlertDialogTitle>
            <AlertDialogDescription>Tato akce je nevratná.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Zrušit</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-[#FF6B6B] hover:bg-[#FF5252]">Smazat</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!pwdResetUser} onOpenChange={(open) => { if (!open) setPwdResetUser(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound size={18} className="text-amber-500" /> Resetovat heslo
            </DialogTitle>
          </DialogHeader>
          {pwdResetUser && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
                Nastavujete nové heslo pro <strong>{pwdResetUser.email}</strong>. Uživatele informujte sami — nové heslo se nikam neposílá.
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-pwd">Nové heslo (min. 8 znaků)</Label>
                <Input
                  id="new-pwd"
                  type="password"
                  value={newPwd}
                  onChange={(e) => setNewPwd(e.target.value)}
                  className="rounded-xl"
                  data-testid="reset-pwd-new"
                  autoComplete="new-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-pwd-2">Heslo znovu</Label>
                <Input
                  id="new-pwd-2"
                  type="password"
                  value={newPwd2}
                  onChange={(e) => setNewPwd2(e.target.value)}
                  className="rounded-xl"
                  data-testid="reset-pwd-confirm"
                  autoComplete="new-password"
                />
              </div>
              {newPwd && newPwd2 && newPwd !== newPwd2 && (
                <div className="text-sm text-red-600">Hesla se neshodují</div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdResetUser(null)} className="rounded-full">Zrušit</Button>
            <Button
              onClick={async () => {
                if (newPwd.length < 8) { toast.error('Heslo musí mít alespoň 8 znaků'); return; }
                if (newPwd !== newPwd2) { toast.error('Hesla se neshodují'); return; }
                setPwdSaving(true);
                try {
                  await usersAPI.resetPassword(pwdResetUser.id, newPwd);
                  toast.success(`Heslo pro ${pwdResetUser.email} bylo resetováno`);
                  setPwdResetUser(null);
                  setNewPwd(''); setNewPwd2('');
                } catch (e) {
                  const msg = e?.response?.data?.detail;
                  toast.error(typeof msg === 'string' ? msg : 'Nepodařilo se resetovat heslo');
                } finally {
                  setPwdSaving(false);
                }
              }}
              disabled={pwdSaving || newPwd.length < 8 || newPwd !== newPwd2}
              className="rounded-full bg-[#4ECDC4] hover:bg-[#3DBDB5]"
              data-testid="reset-pwd-submit"
            >
              {pwdSaving ? 'Ukládání…' : 'Resetovat heslo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersAdmin;
